// src/TspUpload.js
import React, { useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import dataStore from "./dataStore";
import { parseCSV } from "./utils/csv";        // <- make sure this path matches your file
import { showToast } from "./toast";           // named export

const TABLES = [
  { key: "farmers",               label: "Farmers" },
  { key: "plots",                 label: "Plots" },
  { key: "crops",                 label: "Crops" },
  { key: "biochar_applications",  label: "Biochar applications" },
  { key: "mrv_samples",           label: "Soil samples (MRV)" },
];

export default function TspUpload() {
  const { user } = useAuth();
  const role = user?.role || "Farmer";
  const canUse = role === "Admin" || role === "Partner";

  const [table, setTable] = useState("farmers");
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);             // parsed rows
  const [err, setErr] = useState("");

  const existingCount = dataStore.count ? dataStore.count(table) : dataStore.list(table).length;

  const headers = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const set = new Set();
    rows.forEach((r) => Object.keys(r || {}).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [rows]);

  if (!canUse) {
    return (
      <div className="card">
        <h2>TSP Upload</h2>
        <p style={{ color: "#b91c1c" }}>
          Access denied. Only <b>Admin</b> and <b>Partner</b> can use TSP bulk upload.
        </p>
      </div>
    );
  }

  const onPick = async (e) => {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    try {
      const result = await parseCSV(file);
      // Support both shapes: { rows, error } or { headers, rows }
      const parsedRows = Array.isArray(result?.rows) ? result.rows : [];
      if (!parsedRows.length) {
        setRows([]);
        setErr("No data rows found in the CSV (after the header).");
        showToast("No rows found in CSV", "warning");
        return;
      }
      setRows(parsedRows);
      showToast(`Parsed ${parsedRows.length} row(s) from ${file.name}`, "success");
    } catch (e2) {
      console.error(e2);
      setRows([]);
      setErr("Could not read/parse the CSV file.");
      showToast("Failed to parse CSV", "error");
    } finally {
      // allow picking the same file again
      e.target.value = "";
    }
  };

  const onClear = () => {
    setFileName("");
    setRows([]);
    setErr("");
  };

  const onImport = () => {
    if (!Array.isArray(rows) || rows.length === 0) {
      showToast("Nothing to import", "warning");
      return;
    }
    try {
      const { inserted = 0, updated = 0 } = dataStore.bulkInsertAuto(table, rows);
      showToast(`Imported ${inserted} new, updated ${updated}`, "success");
      onClear();
    } catch (e) {
      console.error(e);
      showToast("Import failed", "error");
    }
  };

  return (
    <div className="card">
      <h2>TSP Upload</h2>

      {/* top bar */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label>
          <span style={{ marginRight: 6 }}>Import into</span>
          <select value={table} onChange={(e) => setTable(e.target.value)}>
            {TABLES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          <input
            type="file"
            accept=".csv"
            onChange={onPick}
            style={{ display: "inline-block" }}
          />
        </label>

        <button onClick={onImport} disabled={!rows || rows.length === 0}>
          Import CSV
        </button>
        <button onClick={onClear} disabled={!rows || rows.length === 0}>
          Clear
        </button>

        <span style={{ marginLeft: "auto", color: "#555" }}>
          Existing in <b>{table}</b>: {existingCount}
        </span>
      </div>

      {/* file info + errors */}
      {(fileName || err) && (
        <div
          className="card"
          style={{ padding: 10, margin: "8px 0", background: "#fafafa" }}
        >
          {fileName && (
            <div style={{ marginBottom: 6 }}>
              <b>Selected file:</b> {fileName}
            </div>
          )}
          {err && (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                padding: 8,
                borderRadius: 6,
              }}
            >
              {err}
            </div>
          )}
        </div>
      )}

      {/* preview */}
      {Array.isArray(rows) && rows.length > 0 && (
        <>
          <div style={{ margin: "8px 0 6px 0", color: "#555" }}>
            Previewing first {Math.min(rows.length, 20)} of {rows.length} rows
          </div>
          <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", minWidth: 900, width: "100%" }}>
              <thead>
                <tr>
                  {headers.map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "8px 10px",
                        borderBottom: "1px solid #ddd",
                        background: "#fafafa",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 20).map((r, i) => (
                  <tr key={i}>
                    {headers.map((h) => (
                      <td key={h} style={{ padding: "8px 10px", borderBottom: "1px solid #eee" }}>
                        {toCell(r?.[h])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!err && (!rows || rows.length === 0) && (
        <p style={{ color: "#777", marginTop: 12 }}>
          Choose a CSV file to see a preview, then click <b>Import CSV</b>.
        </p>
      )}
    </div>
  );
}

// small helper for preview
function toCell(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}